/* Minimal DOM helpers. No framework — the design is plain HTML/CSS and the app
 * is small enough that a hyperscript helper plus targeted re-renders is less
 * machinery than a runtime would be. */

/**
 * h('div.klass', { onclick }, child, child)
 * Tag supports `.class` and `#id` shorthand.
 */
export function h(tag, props, ...kids) {
  const [name, ...rest] = String(tag).split(/(?=[.#])/);
  const el = document.createElement(name || 'div');

  for (const token of rest) {
    if (token[0] === '.') el.classList.add(token.slice(1));
    else if (token[0] === '#') el.id = token.slice(1);
  }

  /* Distinguish a props object from a first child by SHAPE, not truthiness.
   * Testing `if (props && …)` silently swallowed falsy children — `h('div', 0)`
   * rendered nothing, which is how a cost of 0 vanished from card tiles. */
  const isProps = props != null
    && typeof props === 'object'
    && !(props instanceof Node)
    && !Array.isArray(props);
  if (!isProps) {
    kids.unshift(props);
    props = null;
  }

  for (const [k, v] of Object.entries(props || {})) {
    if (v == null || v === false) continue;
    if (k === 'class') el.className += (el.className ? ' ' : '') + v;
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else if (k === 'dataset') Object.assign(el.dataset, v);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    /* There is deliberately no `html` prop.
     *
     * There used to be one, assigning straight to innerHTML with no
     * restriction — and nothing in the app ever used it. Every string that
     * reaches the DOM here goes through createTextNode in `add()` below, which
     * is what makes card names, effect text and deck names safe to render even
     * though they arrive from a third-party dataset and a URL hash.
     *
     * That matters more once decks are stored server-side and one user's deck
     * name renders in another user's browser. If you need markup, build it
     * with h() rather than reintroducing this. */
    else if (k in el && k !== 'list' && typeof v !== 'boolean') el[k] = v;
    else el.setAttribute(k, v === true ? '' : v);
  }

  add(el, kids);
  return el;
}

function add(el, kids) {
  for (const kid of kids.flat(Infinity)) {
    if (kid == null || kid === false) continue;
    el.appendChild(kid instanceof Node ? kid : document.createTextNode(String(kid)));
  }
}

export const frag = (...kids) => { const f = document.createDocumentFragment(); add(f, kids); return f; };

/** Replace an element's children in one shot. */
export function fill(el, ...kids) {
  if (!el) return el;
  el.textContent = '';
  add(el, kids);
  return el;
}

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

/**
 * Batch DOM work into the next frame so a burst of state changes paints once.
 *
 * Keeps the LATEST arguments, not the first. The previous version dropped
 * every call after the first in a burst along with its arguments, so a
 * coalesced paint could run against stale data — the one thing a batching
 * helper must not do.
 */
export function scheduler(fn) {
  let queued = false;
  let latest = [];
  return (...args) => {
    latest = args;
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; fn(...latest); });
  };
}

/**
 * Delay work until the caller stops calling for `ms`.
 *
 * For work too heavy to run per keystroke — the card search rebuilds up to 240
 * tiles and attaches a preview listener to each. `scheduler` coalesces to one
 * call per frame, which is still one full rebuild per keystroke for anyone
 * typing at a normal speed.
 */
export function debounce(fn, ms = 120) {
  let timer = null;
  const wrapped = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
  wrapped.cancel = () => clearTimeout(timer);
  wrapped.flush = (...args) => { clearTimeout(timer); fn(...args); };
  return wrapped;
}

/* ---------------------------------------------------------------------- */
/* toasts                                                                  */
/* ---------------------------------------------------------------------- */

let toastHost = null;

export function toast(message, kind = '') {
  if (!toastHost) {
    /* Masked wholesale for session replay: "Signed in as <email>" goes through
     * here, and picking the sensitive toasts one at a time is a rule someone
     * eventually forgets. Recordings still show that a toast appeared and what
     * kind it was, which is the part worth watching. See src/analytics.js. */
    toastHost = h('div#toasts', { 'data-clarity-mask': 'true' });
    document.body.appendChild(toastHost);
  }
  const el = h('div.toast', { class: kind ? `is-${kind}` : '' }, message);
  toastHost.appendChild(el);
  setTimeout(() => {
    el.style.transition = 'opacity .25s, transform .25s';
    el.style.opacity = '0';
    el.style.transform = 'translateY(8px)';
    setTimeout(() => el.remove(), 260);
  }, 2200);
}

/* ---------------------------------------------------------------------- */
/* misc                                                                    */
/* ---------------------------------------------------------------------- */

export const fmt = n => Number(n || 0).toLocaleString();

/**
 * Screen-reader announcements for state that is otherwise only visual.
 *
 * Assigning the same text to a live region twice announces once — the region
 * only fires on *change* — so a repeated message was silently swallowed.
 * "Not a legal target" said twice in a row is exactly the case where the user
 * most needs to hear it the second time. Clear the region, then set the text
 * on the next frame so the change is observed.
 */
let liveRegion = null;
let announceTimer = null;
export function announce(msg) {
  if (!liveRegion) {
    liveRegion = h('div.sr-only', { role: 'status', 'aria-live': 'polite' });
    document.body.appendChild(liveRegion);
  }
  const text = String(msg ?? '');
  if (liveRegion.textContent !== text) { liveRegion.textContent = text; return; }

  clearTimeout(announceTimer);
  liveRegion.textContent = '';
  announceTimer = setTimeout(() => { liveRegion.textContent = text; }, 0);
}
